import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Select, Badge } from '@tkpod/ui';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { getCsrfToken } from '@/lib/csrf';
import type { TemplateDetail } from '@/pages/CatalogDetailPage';

interface TemplateImageItem {
  url: string;
  thumbnailUrl: string;
  type: string;
  isDefault: boolean;
}

const STAT_TABS_BY_TYPE: Record<string, string[]> = {
  APPAREL: ['options', 'presets'],
  DTF_BY_SIZE: ['presets'],
  GANGSHEET: ['presets'],
  UV_STICKER: ['presets'],
  DRINKWARE: ['options', 'presets'],
  ACCESSORIES: ['options', 'presets'],
  DEFAULT: ['options', 'presets'],
};

const STAT_LABELS: Record<string, string> = {
  options: 'Options',
  presets: 'Presets',
};

function getStatValue(template: TemplateDetail, tab: string): number | null {
  switch (tab) {
    case 'options': return template.options?.length ?? 0;
    case 'presets': return template.presetCount;
    default: return null;
  }
}

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const TYPE_OPTIONS = [
  { value: 'DTF_BY_SIZE', label: 'DTF by Size' },
  { value: 'GANGSHEET', label: 'Gang Sheet' },
  { value: 'UV_STICKER', label: 'UV Sticker' },
  { value: 'APPAREL', label: 'Apparel' },
  { value: 'DRINKWARE', label: 'Drinkware' },
  { value: 'ACCESSORIES', label: 'Accessories' },
  { value: 'HOME_DECOR', label: 'Home Decor' },
  { value: 'STATIONERY', label: 'Stationery' },
  { value: 'CUSTOM', label: 'Custom' },
];

const DESIGNER_MODE_OPTIONS = [
  { value: 'NONE', label: 'No Designer' },
  { value: 'DTF_BY_SIZE', label: 'DTF by Size' },
  { value: 'GANGSHEET', label: 'Gang Sheet' },
  { value: 'APPAREL', label: 'Apparel (multi-side)' },
  { value: 'PRODUCT_MOCKUP', label: 'Product Mockup' },
];

interface CategoryItem {
  id: string;
  name: string;
  slug: string;
}

const TECHNIQUE_OPTIONS = [
  'DTG', 'DTF', 'SUBLIMATION', 'SCREEN_PRINT',
  'EMBROIDERY', 'VINYL', 'UV_PRINT', 'LASER_ENGRAVE',
] as const;

export function CatalogOverviewTab({ template, onDirtyChange }: { template: TemplateDetail; onDirtyChange?: (dirty: boolean) => void }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const setEditingWithDirty = (value: boolean) => {
    setEditing(value);
    onDirtyChange?.(value);
  };
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? '');
  const [status, setStatus] = useState(template.status);
  const [productType, setProductType] = useState(template.productType);
  const [designerMode, setDesignerMode] = useState(template.designerMode);
  const [categoryId, setCategoryId] = useState(template.categoryId ?? '');
  const [brand, setBrand] = useState(template.brand ?? '');

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery<{ data: CategoryItem[] }>({
    queryKey: ['catalog-categories', 'active'],
    queryFn: () => api.get<{ data: CategoryItem[] }>('/api/v1/admin/catalog/categories?activeOnly=true'),
    enabled: editing,
  });
  const categories = categoriesData?.data ?? [];
  const categoryOptions = [
    { value: '', label: 'No Category' },
    ...categories.map((c) => ({ value: c.id, label: c.name })),
  ];
  const [modelName, setModelName] = useState(template.modelName ?? '');
  const [material, setMaterial] = useState(template.material ?? '');
  const [fabricWeight, setFabricWeight] = useState(template.fabricWeight ?? '');
  const [originCountry, setOriginCountry] = useState(template.originCountry ?? '');
  const [techniques, setTechniques] = useState<string[]>(template.techniques ?? []);
  const [avgFulfillmentDays, setAvgFulfillmentDays] = useState(
    template.avgFulfillmentDays != null ? String(template.avgFulfillmentDays) : '',
  );
  const [isDiscontinued, setIsDiscontinued] = useState(template.isDiscontinued ?? false);

  useEffect(() => {
    if (!editing) {
      setName(template.name);
      setDescription(template.description ?? '');
      setStatus(template.status);
      setProductType(template.productType);
      setDesignerMode(template.designerMode);
      setCategoryId(template.categoryId ?? '');
      setBrand(template.brand ?? '');
      setModelName(template.modelName ?? '');
      setMaterial(template.material ?? '');
      setFabricWeight(template.fabricWeight ?? '');
      setOriginCountry(template.originCountry ?? '');
      setTechniques(template.techniques ?? []);
      setAvgFulfillmentDays(template.avgFulfillmentDays != null ? String(template.avgFulfillmentDays) : '');
      setIsDiscontinued(template.isDiscontinued ?? false);
    }
  }, [editing, template.updatedAt]);

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/v1/admin/catalog/templates/${template.id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-template', template.id] });
      setEditingWithDirty(false);
      toast('Template updated successfully.');
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to update template.', 'error');
    },
  });

  const resetForm = () => {
    setName(template.name);
    setDescription(template.description ?? '');
    setStatus(template.status);
    setProductType(template.productType);
    setDesignerMode(template.designerMode);
    setCategoryId(template.categoryId ?? '');
    setBrand(template.brand ?? '');
    setModelName(template.modelName ?? '');
    setMaterial(template.material ?? '');
    setFabricWeight(template.fabricWeight ?? '');
    setOriginCountry(template.originCountry ?? '');
    setTechniques(template.techniques ?? []);
    setAvgFulfillmentDays(template.avgFulfillmentDays != null ? String(template.avgFulfillmentDays) : '');
    setIsDiscontinued(template.isDiscontinued ?? false);
  };

  const toggleTechnique = (tech: string) => {
    setTechniques((prev) =>
      prev.includes(tech) ? prev.filter((t) => t !== tech) : [...prev, tech],
    );
  };

  const handleSave = () => {
    const changes: Record<string, unknown> = {};
    if (name !== template.name) changes.name = name;
    if (description !== (template.description ?? '')) changes.description = description || null;
    if (status !== template.status) changes.status = status;
    if (productType !== template.productType) changes.type = productType;
    if (designerMode !== template.designerMode) changes.designerMode = designerMode;
    const newCatId = categoryId || null;
    if (newCatId !== (template.categoryId ?? null)) changes.categoryId = newCatId;
    if (brand !== (template.brand ?? '')) changes.brand = brand || null;
    if (modelName !== (template.modelName ?? '')) changes.modelName = modelName || null;
    if (material !== (template.material ?? '')) changes.material = material || null;
    if (fabricWeight !== (template.fabricWeight ?? '')) changes.fabricWeight = fabricWeight || null;
    if (originCountry !== (template.originCountry ?? '')) changes.originCountry = originCountry || null;
    if (JSON.stringify(techniques) !== JSON.stringify(template.techniques ?? [])) changes.techniques = techniques;
    const rawParsedDays = avgFulfillmentDays ? parseInt(avgFulfillmentDays, 10) : null;
    const parsedDays = rawParsedDays !== null && !isNaN(rawParsedDays) ? rawParsedDays : null;
    if (parsedDays !== (template.avgFulfillmentDays ?? null)) changes.avgFulfillmentDays = parsedDays;
    if (isDiscontinued !== (template.isDiscontinued ?? false)) changes.isDiscontinued = isDiscontinued;
    if (Object.keys(changes).length === 0) { setEditingWithDirty(false); return; }
    updateMutation.mutate(changes);
  };

  return (
    <Card>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">Template Details</h3>
          {!editing ? (
            <Button size="sm" variant="outline" onClick={() => setEditingWithDirty(true)}>Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditingWithDirty(false); resetForm(); }}>
                Cancel
              </Button>
            </div>
          )}
        </div>

        {updateMutation.isError && (
          <div className="mb-4 rounded-md bg-danger-50 p-3 text-sm text-danger-700">
            {(updateMutation.error as Error)?.message || 'Failed to update.'}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-text-secondary">Name {editing && <span className="text-danger-500">*</span>}</label>
            {editing ? (
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{template.name}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Slug</label>
            <p className="mt-0.5 text-sm text-text-primary">{template.slug}</p>
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Status</label>
            {editing ? (
              <Select value={status} onChange={(e) => setStatus(e.target.value)} options={STATUS_OPTIONS} />
            ) : (
              <div className="mt-0.5"><Badge variant={template.status === 'ACTIVE' ? 'success' : template.status === 'DRAFT' ? 'warning' : 'default'} size="sm">{template.status}</Badge></div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Type</label>
            {editing ? (
              <Select value={productType} onChange={(e) => setProductType(e.target.value)} options={TYPE_OPTIONS} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{template.productType.replace(/_/g, ' ')}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Designer Mode</label>
            {editing ? (
              <Select value={designerMode} onChange={(e) => setDesignerMode(e.target.value)} options={DESIGNER_MODE_OPTIONS} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{template.designerMode}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Design Context</label>
            <p className="mt-0.5 text-sm text-text-primary">{template.designContext}</p>
            <p className="mt-0.5 text-xs text-text-tertiary">
              Immutable after create. Duplicate this template to pick a
              designer context for the copy (Storefront defaults to Product).
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Category</label>
            {editing ? (
              categoriesLoading ? (
                <div className="mt-1 h-9 w-full animate-pulse rounded-md bg-surface-tertiary" />
              ) : (
                <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} options={categoryOptions} />
              )
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{template.categoryName ?? '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Version</label>
            <p className="mt-0.5 text-sm text-text-primary">{template.version}</p>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-text-secondary">Description</label>
            {editing ? (
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{template.description ?? '—'}</p>
            )}
          </div>
        </div>

        <div className="mt-6 border-t border-border pt-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Product Information</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-text-secondary">Brand</label>
              {editing ? (
                <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Bella+Canvas" />
              ) : (
                <p className="mt-0.5 text-sm text-text-primary">{template.brand ?? '—'}</p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-text-secondary">Model Name</label>
              {editing ? (
                <Input value={modelName} onChange={(e) => setModelName(e.target.value)} placeholder="e.g. 3001" />
              ) : (
                <p className="mt-0.5 text-sm text-text-primary">{template.modelName ?? '—'}</p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-text-secondary">Material</label>
              {editing ? (
                <Input value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="e.g. 100% combed ring-spun cotton" />
              ) : (
                <p className="mt-0.5 text-sm text-text-primary">{template.material ?? '—'}</p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-text-secondary">Fabric Weight</label>
              {editing ? (
                <Input value={fabricWeight} onChange={(e) => setFabricWeight(e.target.value)} placeholder="e.g. 4.2 oz/yd²" />
              ) : (
                <p className="mt-0.5 text-sm text-text-primary">{template.fabricWeight ?? '—'}</p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-text-secondary">Origin Country</label>
              {editing ? (
                <Input value={originCountry} onChange={(e) => setOriginCountry(e.target.value)} placeholder="e.g. Nicaragua" />
              ) : (
                <p className="mt-0.5 text-sm text-text-primary">{template.originCountry ?? '—'}</p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-text-secondary">Avg. Fulfillment Days</label>
              {editing ? (
                <Input
                  type="number"
                  min="0"
                  value={avgFulfillmentDays}
                  onChange={(e) => setAvgFulfillmentDays(e.target.value)}
                  placeholder="e.g. 3"
                />
              ) : (
                <p className="mt-0.5 text-sm text-text-primary">
                  {template.avgFulfillmentDays != null ? `${template.avgFulfillmentDays} days` : '—'}
                </p>
              )}
            </div>

            <div className="flex items-end pb-1">
              <label className="text-xs font-medium text-text-secondary">
                {editing ? (
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isDiscontinued}
                      onChange={(e) => setIsDiscontinued(e.target.checked)}
                      className="rounded border-border"
                    />
                    Discontinued
                  </span>
                ) : (
                  <>
                    Discontinued
                    <p className="mt-0.5 text-sm font-normal text-text-primary">{template.isDiscontinued ? 'Yes' : 'No'}</p>
                  </>
                )}
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-medium text-text-secondary">Print Techniques</label>
              {editing ? (
                <div className="mt-1 flex flex-wrap gap-2">
                  {TECHNIQUE_OPTIONS.map((tech) => (
                    <label key={tech} className="flex items-center gap-1.5 text-xs">
                      <input
                        type="checkbox"
                        checked={techniques.includes(tech)}
                        onChange={() => toggleTechnique(tech)}
                        className="rounded border-border"
                      />
                      {tech.replace(/_/g, ' ')}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="mt-0.5 flex flex-wrap gap-1.5">
                  {(template.techniques ?? []).length > 0 ? (
                    (template.techniques ?? []).map((tech) => (
                      <Badge key={tech} variant="default" size="sm">{tech.replace(/_/g, ' ')}</Badge>
                    ))
                  ) : (
                    <p className="text-sm text-text-primary">—</p>
                  )}
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <TemplateGalleryManager
                templateId={template.id}
                images={(template.images ?? []) as TemplateImageItem[]}
                onUpdate={() => queryClient.invalidateQueries({ queryKey: ['catalog-template', template.id] })}
              />
            </div>
          </div>
        </div>

        {(() => {
          const activeTabs = STAT_TABS_BY_TYPE[template.productType] ?? STAT_TABS_BY_TYPE.DEFAULT;
          const statTabs = activeTabs
            .map((tab) => ({ tab, label: STAT_LABELS[tab], value: getStatValue(template, tab) }))
            .filter((s) => s.value !== null);

          const cols = statTabs.length + 1;
          const gridClass = cols <= 2 ? 'grid-cols-2' : cols <= 3 ? 'grid-cols-3' : 'grid-cols-4';

          return (
            <div className={`mt-6 grid ${gridClass} gap-4 border-t border-border pt-4`}>
              {statTabs.map((s) => (
                <div key={s.tab} className="text-center">
                  <p className="text-2xl font-bold text-text-primary">{s.value}</p>
                  <p className="text-xs text-text-secondary">{s.label}</p>
                </div>
              ))}
              <div className="text-center">
                <p className="text-xs text-text-secondary">Created</p>
                <p className="text-sm text-text-primary">{new Date(template.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                <p className="text-xs text-text-secondary mt-1">Updated</p>
                <p className="text-sm text-text-primary">{new Date(template.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
          );
        })()}
      </div>
    </Card>
  );
}

// =============================================================================
// Template Gallery Manager
// =============================================================================

const IMAGE_TYPES = ['front', 'back', 'lifestyle', 'detail', 'other'] as const;

function TemplateGalleryManager({
  templateId,
  images,
  onUpdate,
}: {
  templateId: string;
  images: TemplateImageItem[];
  onUpdate: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const baseUrl = import.meta.env.VITE_API_URL as string;
      const headers: Record<string, string> = {};
      const csrfToken = getCsrfToken();
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

      const res = await fetch(
        `${baseUrl}/api/v1/admin/catalog/templates/${templateId}/images`,
        { method: 'POST', credentials: 'include', headers, body: formData },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message ?? 'Upload failed');
      }
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (index: number) => {
    setError(null);
    try {
      await api.delete(`/api/v1/admin/catalog/templates/${templateId}/images/${index}`);
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleSetDefault = async (index: number) => {
    setError(null);
    try {
      await api.patch(`/api/v1/admin/catalog/templates/${templateId}/images/default`, { index });
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set default');
    }
  };

  const handleTypeChange = async (index: number, type: string) => {
    try {
      await api.patch(`/api/v1/admin/catalog/templates/${templateId}/images/${index}`, { type });
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update type');
    }
  };

  return (
    <div>
      <label className="text-xs font-medium text-text-secondary">Product Images</label>

      {error && (
        <div className="mt-1 rounded bg-danger-50 px-2 py-1 text-xs text-danger-700">
          {error}
          <button type="button" onClick={() => setError(null)} className="ml-2 font-semibold hover:underline">Dismiss</button>
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-3">
        {images.map((img, idx) => (
          <div key={img.url} className="flex flex-col items-center gap-1">
            <div
              className={`group relative overflow-hidden rounded-lg border-2 transition-all ${
                img.isDefault
                  ? 'border-brand-500 ring-2 ring-brand-500/20'
                  : 'border-border hover:border-brand-300'
              }`}
            >
              <img
                src={img.thumbnailUrl || img.url}
                alt={img.type}
                className="h-20 w-20 object-cover"
              />

              {img.isDefault && (
                <span className="absolute left-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-500 text-[8px] text-white" title="Default image">
                  ★
                </span>
              )}

              <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/0 opacity-0 transition-all group-hover:bg-black/50 group-hover:opacity-100">
                {!img.isDefault && (
                  <button
                    type="button"
                    onClick={() => handleSetDefault(idx)}
                    className="rounded bg-white/90 p-1 text-[10px] font-semibold text-brand-700 hover:bg-white"
                    title="Set as default"
                  >
                    ★
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(idx)}
                  className="rounded bg-white/90 p-1 text-[10px] font-semibold text-danger-700 hover:bg-white"
                  title="Delete"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Type label selector */}
            <select
              value={img.type}
              onChange={(e) => handleTypeChange(idx, e.target.value)}
              className="w-20 rounded border-0 bg-transparent px-0 py-0 text-center text-[10px] font-medium text-text-tertiary hover:text-text-primary cursor-pointer focus:ring-0"
            >
              {IMAGE_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        ))}

        {/* Upload button */}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border bg-surface-secondary text-text-tertiary transition-colors hover:border-brand-400 hover:bg-brand-50 hover:text-brand-600 disabled:opacity-50"
        >
          {uploading ? (
            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60 30" />
            </svg>
          ) : (
            <>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              <span className="text-[9px] font-medium">Add Image</span>
            </>
          )}
        </button>

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUpload(file);
          }}
          className="hidden"
        />
      </div>
    </div>
  );
}
